import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal,
  computed
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { trigger, transition, style, animate } from '@angular/animations';
import { FormsModule } from '@angular/forms';
import { InputText } from 'primeng/inputtext';
import { InputNumber } from 'primeng/inputnumber';
import { DatePicker } from 'primeng/datepicker';
import { Button } from 'primeng/button';
import { Badge } from 'primeng/badge';
import { ConfirmationService, PrimeTemplate } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { PropertyWizardStateService } from '../../../services';
import {
  Season,
  UnitSeasonPrice,
  SEASON_PRESETS,
  SEASON_COLORS,
  UnitType,
  OccupancyPricing,
  OccupancyPricingMode
} from '../../../models';

/** Wycena za mniej osób dla jednego typu w formularzu sezonu. */
interface UnitOccupancyForm {
  mode: OccupancyPricingMode;
  /** liczba osób → wartość (amount: cena/noc w PLN; percent: rabat % 0..50). */
  values: Map<number, number>;
}

interface SeasonFormData {
  name: string;
  startDate: Date | null;
  endDate: Date | null;
  color: string;
  minStay: number | null;
  unitPrices: Map<string, number | null>;
  /** unitTypeId → wycena za mniej osób (tryb + wartości per liczba osób). */
  occupancyPricing: Map<string, UnitOccupancyForm>;
}

interface SeasonDateInfo {
  color: string;
  name: string;
}

@Component({
  selector: 'app-season-pricing',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    FormsModule,
    InputText,
    InputNumber,
    DatePicker,
    Button,
    Badge,
    PrimeTemplate,
    ConfirmDialogModule,
    TranslocoModule
  ],
  providers: [ConfirmationService],
  templateUrl: './season-pricing.component.html',
  styleUrl: './season-pricing.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideDown', [
      transition(':enter', [
        style({ height: 0, opacity: 0, overflow: 'hidden' }),
        animate('250ms ease-out', style({ height: '*', opacity: 1, overflow: 'visible' }))
      ]),
      transition(':leave', [
        style({ overflow: 'hidden' }),
        animate('200ms ease-in', style({ height: 0, opacity: 0 }))
      ])
    ]),
    trigger('fadeSlideIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-8px)' }),
        animate('200ms 50ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      transition(':leave', [
        animate('150ms ease-in', style({ opacity: 0, transform: 'translateY(-8px)' }))
      ])
    ])
  ]
})
export class SeasonPricingComponent {
  private readonly stateService = inject(PropertyWizardStateService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly transloco = inject(TranslocoService);

  readonly seasons = this.stateService.seasons;
  readonly unitTypes = this.stateService.unitTypes;
  readonly isViewMode = this.stateService.isViewMode;
  readonly presets = SEASON_PRESETS;
  readonly today = new Date();

  readonly isGuideVisible = signal(false);

  readonly formData = signal<SeasonFormData>({
    name: '',
    startDate: null,
    endDate: null,
    color: SEASON_COLORS[0],
    minStay: null,
    unitPrices: new Map(),
    occupancyPricing: new Map()
  });

  readonly editingSeasonId = signal<string | null>(null);
  readonly isAddFormVisible = signal(false);

  readonly isFormValid = computed(() => {
    const data = this.formData();
    return data.name.trim().length > 0 &&
           data.startDate !== null &&
           data.endDate !== null &&
           data.startDate <= data.endDate;
  });

  readonly isEditing = computed(() => this.editingSeasonId() !== null);
  readonly isAnyFormOpen = computed(() => this.isEditing() || this.isAddFormVisible());

  readonly otherSeasons = computed(() => {
    const editingId = this.editingSeasonId();
    return this.seasons().filter(s => s.id !== editingId);
  });

  readonly disabledDates = computed(() => {
    const dates: Date[] = [];
    for (const season of this.otherSeasons()) {
      const current = new Date(season.startDate);
      current.setHours(0, 0, 0, 0);
      const end = new Date(season.endDate);
      end.setHours(0, 0, 0, 0);
      while (current <= end) {
        dates.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
    }
    return dates;
  });

  readonly endDateMax = computed(() => {
    const startDate = this.formData().startDate;
    if (!startDate) return null;

    const others = this.otherSeasons();
    let nearest: Date | null = null;

    for (const season of others) {
      const sStart = new Date(season.startDate);
      sStart.setHours(0, 0, 0, 0);
      if (sStart > startDate) {
        if (!nearest || sStart < nearest) {
          nearest = sStart;
        }
      }
    }

    if (!nearest) return null;

    const maxDate = new Date(nearest);
    maxDate.setDate(maxDate.getDate() - 1);
    return maxDate;
  });

  private readonly seasonDateInfoMap = computed(() => {
    const map = new Map<string, SeasonDateInfo>();
    for (const season of this.otherSeasons()) {
      const current = new Date(season.startDate);
      current.setHours(0, 0, 0, 0);
      const end = new Date(season.endDate);
      end.setHours(0, 0, 0, 0);
      while (current <= end) {
        const key = `${current.getFullYear()}-${current.getMonth()}-${current.getDate()}`;
        map.set(key, { color: season.color, name: season.name });
        current.setDate(current.getDate() + 1);
      }
    }
    return map;
  });

  getSeasonColor(date: { day: number; month: number; year: number }): string | null {
    const key = `${date.year}-${date.month}-${date.day}`;
    return this.seasonDateInfoMap().get(key)?.color ?? null;
  }

  getSeasonName(date: { day: number; month: number; year: number }): string | null {
    const key = `${date.year}-${date.month}-${date.day}`;
    return this.seasonDateInfoMap().get(key)?.name ?? null;
  }

  private getNextColor(): string {
    const usedColors = new Set(this.seasons().map(s => s.color));
    const availableColor = SEASON_COLORS.find(c => !usedColors.has(c));
    return availableColor ?? SEASON_COLORS[Math.floor(Math.random() * SEASON_COLORS.length)];
  }

  formatDateRange(start: Date, end: Date): string {
    const formatDate = (d: Date) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    return `${formatDate(start)} → ${formatDate(end)}`;
  }

  hasSeasonPrice(season: Season, unitTypeId: string): boolean {
    return season.unitPrices.some(up => up.unitTypeId === unitTypeId);
  }

  getSeasonPrice(season: Season, unitTypeId: string): number {
    const price = season.unitPrices.find(up => up.unitTypeId === unitTypeId);
    return price?.pricePerNight ?? 0;
  }

  getUnitTypeById(unitTypeId: string): UnitType | undefined {
    return this.unitTypes().find(ut => ut.id === unitTypeId);
  }

  getFormUnitPrice(unitTypeId: string): number | null {
    return this.formData().unitPrices.get(unitTypeId) ?? null;
  }

  setFormUnitPrice(unitTypeId: string, value: number | null): void {
    this.formData.update(data => {
      const newPrices = new Map(data.unitPrices);
      if (value === null) {
        newPrices.delete(unitTypeId);
      } else {
        newPrices.set(unitTypeId, value);
      }
      return { ...data, unitPrices: newPrices };
    });
  }

  /** Wiersze wyceny: liczby osób od maxOccupancy-1 w dół do 1. */
  occupantsRows(unitTypeId: string): number[] {
    const max = this.getUnitTypeById(unitTypeId)?.maxOccupancy ?? 0;
    const rows: number[] = [];
    for (let occ = max - 1; occ >= 1; occ--) rows.push(occ);
    return rows;
  }

  /** Tryb wyceny za mniej osób dla typu w tym sezonie (domyślnie 'amount'). */
  getOccupancyMode(unitTypeId: string): OccupancyPricingMode {
    return this.formData().occupancyPricing.get(unitTypeId)?.mode ?? 'amount';
  }

  /** Zmiana trybu zł/% — czyści wpisane wartości (inna jednostka). */
  setOccupancyMode(unitTypeId: string, mode: OccupancyPricingMode): void {
    this.formData.update(data => {
      const map = new Map(data.occupancyPricing);
      map.set(unitTypeId, { mode, values: new Map() });
      return { ...data, occupancyPricing: map };
    });
  }

  getOccupancyValue(unitTypeId: string, occupants: number): number | null {
    return this.formData().occupancyPricing.get(unitTypeId)?.values.get(occupants) ?? null;
  }

  setOccupancyValue(unitTypeId: string, occupants: number, value: number | null): void {
    this.formData.update(data => {
      const map = new Map(data.occupancyPricing);
      const current = map.get(unitTypeId);
      const mode = current?.mode ?? 'amount';
      const values = new Map(current?.values ?? []);
      if (value == null) {
        values.delete(occupants);
      } else {
        values.set(occupants, value);
      }
      map.set(unitTypeId, { mode, values });
      return { ...data, occupancyPricing: map };
    });
  }

  /** Sufiks/format pola wg trybu. */
  isAmountMode(unitTypeId: string): boolean {
    return this.getOccupancyMode(unitTypeId) === 'amount';
  }

  /**
   * Podpowiedź kwot dla trybu % — cena za komplet pomniejszona o rabat na każdym
   * poziomie z wpisanym procentem. Zwraca [] w trybie kwotowym (podgląd tylko przy %).
   */
  occupancyPercentPreview(unitTypeId: string): { occupants: number; percent: number; amount: number }[] {
    if (this.isAmountMode(unitTypeId)) return [];
    const base = this.getFormUnitPrice(unitTypeId);
    if (base == null || base <= 0) return [];
    const rows: { occupants: number; percent: number; amount: number }[] = [];
    for (const occ of this.occupantsRows(unitTypeId)) {
      const pct = this.getOccupancyValue(unitTypeId, occ);
      if (pct == null || pct <= 0) continue;
      rows.push({ occupants: occ, percent: pct, amount: Math.round(base * (1 - pct / 100)) });
    }
    return rows;
  }

  /** Czy sezon ma ustawioną jakąkolwiek wycenę za mniej osób dla typu (znacznik na liście). */
  seasonHasOccupancyPricing(season: Season, unitTypeId: string): boolean {
    const price = season.unitPrices.find(up => up.unitTypeId === unitTypeId);
    return !!price?.occupancyPricing?.tiers?.some(t => t.value > 0);
  }

  updateFormField<K extends keyof SeasonFormData>(field: K, value: SeasonFormData[K]): void {
    this.formData.update(data => ({ ...data, [field]: value }));
  }

  toggleGuide(): void {
    this.isGuideVisible.update(v => !v);
  }

  applyPreset(preset: typeof SEASON_PRESETS[0]): void {
    const currentYear = new Date().getFullYear();
    const { startDate, endDate } = preset.getDateRange(currentYear);
    this.formData.update(data => ({
      ...data,
      name: data.name || preset.name,
      startDate,
      endDate
    }));
  }

  /**
   * Buduje ceny sezonu z formularza. Wycena za mniej osób: brak wpisów →
   * occupancyPricing = undefined (wszyscy płacą cenę za komplet); są wpisy →
   * { mode, tiers } (tylko poziomy z wartością > 0).
   */
  private buildUnitPrices(data: SeasonFormData): UnitSeasonPrice[] {
    const unitPrices: UnitSeasonPrice[] = [];
    data.unitPrices.forEach((price, unitTypeId) => {
      if (price === null) return;
      const pricing = data.occupancyPricing.get(unitTypeId);
      let occupancyPricing: OccupancyPricing | undefined;
      if (pricing) {
        const tiers = Array.from(pricing.values.entries())
          .filter(([, v]) => v != null && v > 0)
          .map(([occupants, value]) => ({ occupants, value }))
          .sort((a, b) => b.occupants - a.occupants);
        if (tiers.length) occupancyPricing = { mode: pricing.mode, tiers };
      }
      unitPrices.push({ unitTypeId, pricePerNight: price, occupancyPricing });
    });
    return unitPrices;
  }

  addSeason(): void {
    if (!this.isFormValid()) return;

    const data = this.formData();
    const unitPrices = this.buildUnitPrices(data);

    const season: Season = {
      id: crypto.randomUUID(),
      name: data.name.trim(),
      startDate: data.startDate!,
      endDate: data.endDate!,
      color: data.color,
      minStay: data.minStay ?? undefined,
      unitPrices
    };

    this.stateService.addSeason(season);
    this.resetForm();
  }

  showAddForm(): void {
    this.editingSeasonId.set(null);
    this.resetForm();
    this.isAddFormVisible.set(true);
  }

  cancelAdd(): void {
    this.isAddFormVisible.set(false);
    this.resetForm();
  }

  editSeason(season: Season): void {
    this.isAddFormVisible.set(false);

    const unitPricesMap = new Map<string, number | null>();
    const occupancyPricingMap = new Map<string, UnitOccupancyForm>();
    season.unitPrices.forEach(up => {
      unitPricesMap.set(up.unitTypeId, up.pricePerNight);
      if (up.occupancyPricing != null) {
        const values = new Map<number, number>();
        for (const t of up.occupancyPricing.tiers) values.set(t.occupants, t.value);
        occupancyPricingMap.set(up.unitTypeId, { mode: up.occupancyPricing.mode, values });
      }
    });

    // Normalize to local midnight to avoid timezone issues with datepicker maxDate comparison
    const startDate = new Date(season.startDate);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(season.endDate);
    endDate.setHours(0, 0, 0, 0);

    this.formData.set({
      name: season.name,
      startDate,
      endDate,
      color: season.color,
      minStay: season.minStay ?? null,
      unitPrices: unitPricesMap,
      occupancyPricing: occupancyPricingMap
    });

    this.editingSeasonId.set(season.id);
  }

  saveSeason(): void {
    if (!this.isFormValid()) return;

    const data = this.formData();
    const editingId = this.editingSeasonId();
    const unitPrices = this.buildUnitPrices(data);

    if (editingId) {
      const season: Season = {
        id: editingId,
        name: data.name.trim(),
        startDate: data.startDate!,
        endDate: data.endDate!,
        color: data.color,
        minStay: data.minStay ?? undefined,
        unitPrices
      };
      this.stateService.updateSeason(season);
    } else {
      const season: Season = {
        id: crypto.randomUUID(),
        name: data.name.trim(),
        startDate: data.startDate!,
        endDate: data.endDate!,
        color: data.color,
        minStay: data.minStay ?? undefined,
        unitPrices
      };
      this.stateService.addSeason(season);
    }

    this.isAddFormVisible.set(false);
    this.resetForm();
  }

  cancelEdit(): void {
    this.editingSeasonId.set(null);
    this.resetForm();
  }

  removeSeason(seasonId: string): void {
    const season = this.seasons().find(s => s.id === seasonId);
    this.confirmationService.confirm({
      message: this.transloco.translate('objects.wizard.seasons.confirmDeleteMessage', { name: season?.name }),
      header: this.transloco.translate('objects.wizard.seasons.confirmDeleteHeader'),
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      acceptLabel: this.transloco.translate('common.delete'),
      rejectLabel: this.transloco.translate('common.cancel'),
      accept: () => {
        this.stateService.removeSeason(seasonId);
        if (this.editingSeasonId() === seasonId) {
          this.resetForm();
        }
      }
    });
  }

  private resetForm(): void {
    this.formData.set({
      name: '',
      startDate: null,
      endDate: null,
      color: this.getNextColor(),
      minStay: null,
      unitPrices: new Map(),
      occupancyPricing: new Map()
    });
    this.editingSeasonId.set(null);
  }
}
